/**
 * ShareDecisionModal.jsx — Dark Mode + Scrollable Modal (max-h-[90vh])
 */
import { useState } from "react";
import { functions } from "../../firebaseConfig";
import { httpsCallable } from "firebase/functions";
import { FiCopy, FiCheck, FiShare2, FiClock, FiGlobe } from "react-icons/fi";
import { useLanguage } from "../../contexts/LanguageContext";
import { useDarkMode } from "../../hooks/useDarkMode";

const ShareDecisionModal = ({ decisionId, onClose }) => {
  const { t, lang } = useLanguage();
  const { darkMode } = useDarkMode();
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState("");
  const [shareLink,     setShareLink]     = useState(null);
  const [copied,        setCopied]        = useState(false);
  const [expiresInDays, setExpiresInDays] = useState(30);

  const generateShareLink = async () => {
    setError(""); setLoading(true);
    try {
      const createShareLink = httpsCallable(functions, "createShareLink");
      const result = await createShareLink({ decisionId, expiresInDays: parseInt(expiresInDays), lang });
      setShareLink(result.data);
    } catch (err) {
      setError(
        err.message?.includes("not enabled") || err.message?.includes("feature") ? t("share_feature_unavailable")
        : err.message?.includes("quota")     || err.message?.includes("limit")   ? t("share_limit_reached")
        : err.message || t("error")
      );
    } finally { setLoading(false); }
  };

  const copyToClipboard = async () => {
    if (shareLink?.shareUrl) {
      try { await navigator.clipboard.writeText(shareLink.shareUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch {}
    }
  };

  return (
    /* Overlay — centres the modal, NO overflow here */
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
      {/* ↓ KEY CHANGE: max height + scroll lives on the modal box itself */}
      <div className={`
        ${darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}
        border rounded-2xl w-full max-w-md shadow-2xl
        max-h-[90vh] overflow-y-auto
      `}>
        {/* Inner padding wrapper */}
        <div className="p-8">

          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-blue-500/20 p-3 rounded-xl">
              <FiShare2 className="text-blue-400 text-2xl" />
            </div>
            <div>
              <h2 className={`text-2xl font-bold ${darkMode ? "text-white" : "text-gray-900"}`}>{t("share_decision")}</h2>
              <p className={`${darkMode ? "text-gray-400" : "text-gray-500"} text-sm`}>{t("share_decision_desc")}</p>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded-xl mb-4">{error}</div>
          )}

          {!shareLink ? (
            <>
              {/* Language toggle */}
              <div className="mb-4">
                <label className={`block text-sm font-medium mb-2 ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
                  <FiGlobe className="inline mr-1" />{t("link_language") || "Link Language"}
                </label>
                <div className="flex gap-2">
                  <button onClick={() => {}} disabled
                    className={`flex-1 py-2 rounded-xl text-sm font-medium border transition-colors ${lang === "en" ? "bg-blue-600 text-white border-blue-600" : darkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-600 border-gray-300"}`}>
                    English
                  </button>
                  <button onClick={() => {}} disabled
                    className={`flex-1 py-2 rounded-xl text-sm font-medium border transition-colors ${lang === "ar" ? "bg-blue-600 text-white border-blue-600" : darkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-gray-100 text-gray-600 border-gray-300"}`}>
                    العربية
                  </button>
                </div>
                <p className={`${darkMode ? "text-gray-500" : "text-gray-400"} text-xs mt-1`}>{t("link_language_hint") || "Link will open in your current language"}</p>
              </div>

              {/* Expiration */}
              <div className="mb-6">
                <label className={`block text-sm font-medium mb-2 ${darkMode ? "text-gray-300" : "text-gray-700"}`}>{t("link_expiration")}</label>
                <select value={expiresInDays} onChange={e => setExpiresInDays(e.target.value)}
                  className={`w-full border rounded-xl px-4 py-3 focus:outline-none focus:border-blue-500 ${darkMode ? "bg-gray-700/50 border-gray-600 text-white" : "bg-gray-100 border-gray-300 text-gray-900"}`}>
                  <option value="7">7 {t("days")}</option>
                  <option value="14">14 {t("days")}</option>
                  <option value="30">30 {t("days")}</option>
                  <option value="90">90 {t("days")}</option>
                  <option value="0">{t("never_expires")}</option>
                </select>
                <p className={`${darkMode ? "text-gray-400" : "text-gray-500"} text-xs mt-2 flex items-center gap-1`}>
                  <FiClock className="flex-shrink-0" />{t("link_public_until_expiration")}
                </p>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button onClick={onClose}
                  className={`flex-1 py-3 rounded-xl transition-colors ${darkMode ? "bg-gray-700 hover:bg-gray-600 text-white" : "bg-gray-100 hover:bg-gray-200 text-gray-900"}`}>
                  {t("cancel")}
                </button>
                <button onClick={generateShareLink} disabled={loading}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/50 text-white py-3 rounded-xl transition-colors font-semibold">
                  {loading ? t("generating") : t("generate_link")}
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Share URL */}
              <div className="mb-6">
                <label className={`block text-sm font-medium mb-2 ${darkMode ? "text-gray-300" : "text-gray-700"}`}>{t("share_link")}</label>
                <div className="flex gap-2">
                  <input type="text" value={shareLink.shareUrl} readOnly
                    className={`flex-1 border rounded-xl px-4 py-3 focus:outline-none ${darkMode ? "bg-gray-700/50 border-gray-600 text-white" : "bg-gray-100 border-gray-300 text-gray-900"}`} />
                  <button onClick={copyToClipboard}
                    className={`px-4 py-3 rounded-xl transition-colors ${copied ? "bg-green-600 text-white" : darkMode ? "bg-gray-700 hover:bg-gray-600 text-white" : "bg-gray-200 hover:bg-gray-300 text-gray-700"}`}>
                    {copied ? <FiCheck /> : <FiCopy />}
                  </button>
                </div>
                {copied && <p className="text-green-400 text-sm mt-2">{t("copied_to_clipboard")}</p>}
              </div>

              {/* Expiry info */}
              {shareLink.expiresAt && (
                <div className="bg-blue-500/10 border border-blue-500/30 p-4 rounded-xl mb-6">
                  <p className={`${darkMode ? "text-gray-300" : "text-gray-700"} text-sm flex items-center gap-2`}>
                    <FiClock />
                    {t("expires_on")}:{" "}
                    <span className={`${darkMode ? "text-white" : "text-gray-900"} font-semibold`}>
                      {new Date(shareLink.expiresAt).toLocaleDateString("en-US")}
                    </span>
                  </p>
                </div>
              )}

              {/* Share via */}
              <div className="space-y-2 mb-6">
                <p className={`${darkMode ? "text-gray-400" : "text-gray-500"} text-sm`}>{t("share_via")}:</p>
                <div className="flex gap-2">
                  <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareLink.shareUrl)}&text=${encodeURIComponent(t("check_out_decision"))}`}
                    target="_blank" rel="noopener noreferrer"
                    className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-2 rounded-xl text-center text-sm transition-colors">Twitter</a>
                  <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareLink.shareUrl)}`}
                    target="_blank" rel="noopener noreferrer"
                    className="flex-1 bg-blue-700 hover:bg-blue-800 text-white py-2 rounded-xl text-center text-sm transition-colors">LinkedIn</a>
                  <a href={`mailto:?subject=${t("share_decision")}&body=${encodeURIComponent(t("check_out_decision") + ": " + shareLink.shareUrl)}`}
                    className={`flex-1 py-2 rounded-xl text-center text-sm transition-colors ${darkMode ? "bg-gray-700 hover:bg-gray-600 text-white" : "bg-gray-200 hover:bg-gray-300 text-gray-800"}`}>
                    {t("email")}
                  </a>
                </div>
              </div>

              {/* Done */}
              <button onClick={onClose} className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl transition-colors font-semibold">
                {t("done")}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShareDecisionModal;

// <p className="text-gray-300 leading-relaxed">
//   {lang === 'ar' ? (decision.reasonAr || decision.reason) : decision.reason}
// </p>